<script setup>
import { computed } from 'vue';
import { useI18n } from 'vue-i18n';
import { useRoute } from 'vue-router';
import Spinner from 'shared/components/Spinner.vue';

const props = defineProps({
  unrespondedData: {
    type: Object,
    default: () => ({ count: 0, conversations: [] }),
  },
  repeatContacts: {
    type: Object,
    default: () => ({ repeat_count: 0, total_contacts: 0, repeat_rate: 0 }),
  },
  loading: { type: Boolean, default: false },
});

const { t } = useI18n();
const route = useRoute();
const accountId = computed(() => route.params.accountId);

const urgencyClass = computed(() => {
  if (props.unrespondedData.count > 10) return 'border-l-n-ruby-9';
  if (props.unrespondedData.count > 0) return 'border-l-n-amber-9';
  return 'border-l-n-teal-9';
});

const conversationUrl = id =>
  `/app/accounts/${accountId.value}/conversations/${id}`;
</script>

<template>
  <div
    class="flex flex-col rounded-xl shadow outline-1 outline outline-n-container bg-n-solid-2 px-6 py-5 border-l-4"
    :class="urgencyClass"
  >
    <h3 class="mb-4 text-sm font-medium text-n-slate-11 uppercase tracking-wider">
      {{ t('CEO_DASHBOARD.NEEDS_ATTENTION.HEADER') }}
    </h3>
    <div v-if="loading" class="flex items-center justify-center py-8">
      <Spinner />
    </div>
    <div v-else class="grid grid-cols-1 gap-6 md:grid-cols-2">
      <!-- Unresponded Escalations -->
      <div>
        <div class="flex items-center justify-between">
          <h4 class="m-0 text-sm font-medium text-n-slate-11">
            {{ t('CEO_DASHBOARD.NEEDS_ATTENTION.UNRESPONDED') }}
          </h4>
          <span
            class="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-semibold"
            :class="unrespondedData.count > 0 ? 'bg-n-ruby-3 text-n-ruby-11' : 'bg-n-teal-3 text-n-teal-11'"
          >
            {{ unrespondedData.count }}
          </span>
        </div>
        <div
          v-if="unrespondedData.conversations?.length > 0"
          class="mt-3 flex flex-col gap-1.5 max-h-40 overflow-y-auto"
        >
          <router-link
            v-for="conv in unrespondedData.conversations.slice(0, 10)"
            :key="conv.id"
            :to="conversationUrl(conv.id)"
            class="inline-flex items-center gap-2 px-2 py-1 rounded text-xs hover:bg-n-alpha-1 transition-colors"
          >
            <span class="font-medium text-n-blue-11">#{{ conv.id }}</span>
            <span class="text-n-slate-10">{{ conv.status }}</span>
          </router-link>
        </div>
        <p v-else class="mt-2 text-xs text-n-teal-11">
          {{ t('CEO_DASHBOARD.NEEDS_ATTENTION.ALL_CLEAR') }}
        </p>
      </div>

      <!-- Repeat Contacts -->
      <div>
        <h4 class="m-0 text-sm font-medium text-n-slate-11">
          {{ t('CEO_DASHBOARD.NEEDS_ATTENTION.REPEAT_CONTACTS') }}
        </h4>
        <div class="mt-3 space-y-3">
          <div class="flex items-baseline justify-between">
            <span class="text-xs text-n-slate-10">
              {{ t('CEO_DASHBOARD.NEEDS_ATTENTION.REPEAT_RATE') }}
            </span>
            <span class="text-lg font-semibold text-n-slate-12">
              {{ repeatContacts.repeat_rate }}%
            </span>
          </div>
          <div class="flex items-baseline justify-between">
            <span class="text-xs text-n-slate-10">
              {{ t('CEO_DASHBOARD.NEEDS_ATTENTION.REPEAT_COUNT') }}
            </span>
            <span class="text-sm font-medium text-n-slate-12">
              {{ repeatContacts.repeat_count }} / {{ repeatContacts.total_contacts }}
            </span>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>
